import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ExerciseAttemptScreen from "../../../../../../../components/screens/Activity/ExerciseAttemptScreen";
import {
  classrooms as classroomQuerys,
  courses as courseQuerys,
  completions as completionQuerys,
  feedback as feedbackQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useServices from "../../../../../../../hooks/useServices";
import useAudioPlayer from "../../../../../../../hooks/useAudioPlayer";
import {
  highlightSpeechText,
  stopHighlightingSpeechText,
} from "../../../../../../../utils/index";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getClassroom } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getCourseSectionTopicCompletion } from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getCompletionFeedback, createFeedback, updateFeedback, deleteFeedback } from "@nualang/nualang-api-and-queries/APIs/Feedback";
import {
  courses as courseHelpers,
  completions as completionHelpers,
  classrooms as classroomHelpers,
  notifications as notificationHelpers,
  sessionReports as sessionReportHelpers,
} from "../../../../../../../utils/helpers";
import { getBotSessionReport } from "../../../../../../../utils/api/bots/api/SessionReports";
import * as sessionReportQuerys from "../../../../../../../utils/api/bots/hooks/sessionReports";
import config from "../../../../../../../config";

import logger from "../../../../../../../logger";
import { useApp, useSubscription } from "../../../../../../../hooks";

function Attempt(props) {
  const params = useParams();
  const { memberId, courseId, sectionId, topicId, createdAt, classroomId } =
    params;
  const [tts, translate] = useServices();
  const { openSnackbar, startLoading, stopLoading, markCommunityAccessRestricted } =
    useApp();
  const { subscription } = useSubscription();
  const date = new Date(0);

  const {
    authData: { username, attributes },
  } = props;

  const { sendFeedbackEmail } = notificationHelpers;

  const [timeoutArray, setTimeoutArray] = useState([]);

  const classroomQuery = classroomQuerys.useClassroom(
    async (classroomId) => {
      const classroom = await getClassroom(classroomId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroom =
    classroomQuery.isSuccess &&
    classroomQuery.data &&
    classroomQuery.data.classroomId
      ? classroomQuery.data
      : {};

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([classroomQuery.data]);
  }, [classroomQuery.data, markCommunityAccessRestricted]);

  const isClassroomCreator =
    classroom.createdBy === username ||
    (classroom.collaborators &&
      Array.isArray(classroom.collaborators) &&
      (classroom.collaborators.includes(attributes.email) ||
        classroom.collaborators.includes(username)));

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const courseSectionTopicCompletionQuery = completionQuerys.useTopicCompletion(
    async (courseId, sectionId, topicId, memberId, createdAt) => {
      const memberCompletion = await getCourseSectionTopicCompletion(
        courseId,
        sectionId,
        topicId,
        memberId,
        createdAt,
      );
      const formattedCompletion = await completionHelpers.formatCompletion(
        memberCompletion.Item,
        username,
        attributes.preferred_username,
      );
      return formattedCompletion;
    },
    {
      courseId,
      sectionId,
      topicId,
      memberId,
      createdAt,
    },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const exerciseAttempt =
    courseSectionTopicCompletionQuery.isSuccess &&
    courseSectionTopicCompletionQuery.data &&
    courseSectionTopicCompletionQuery.data.courseId
      ? courseSectionTopicCompletionQuery.data
      : {};

  const {
    exercise,
    topicName,
    createdByName,
    courseSectionTopicId,
    courseSectionId,
    botId,
    roleplayId,
  } = exerciseAttempt;

  const { addToQueue, addAudioListToQueue, clearQueue, stop } =
    useAudioPlayer();

  const sessionReportQuery = sessionReportQuerys.useBotSessionReport(
    getBotSessionReport,
    { botId, memberId, createdAt },
    {
      enabled:
        (exercise === "bot" || exercise === "bot-listen-and-speak") && !!botId,
      refetchInterval: (query) =>
        query.state.data?.Item?.status === "pending"
          ? sessionReportHelpers.SESSION_REPORT_POLL_MS
          : false,
    },
  );

  const completionId = `${courseSectionTopicId}|${memberId}|${createdAt}`;

  const completionFeedbackQuery = feedbackQuerys.useCompletionFeedback(
      async (completionId) => {
            const feedback = await getCompletionFeedback(completionId);
            return feedback.Items[0] || {};
          },
      {
        completionId,
      },
      { enabled: !!completionId && !completionId.includes("undefined") },
    );

  const completionFeedback = completionFeedbackQuery.isSuccess && completionFeedbackQuery.data
  ? completionFeedbackQuery.data
  : {};

  const createFeedbackMutation = feedbackQuerys.useCreateFeedback(createFeedback, {
      onMutate: () => {
        startLoading();
      },
      onSettled: () => {
        stopLoading();
      },
      onError: () => {
        openSnackbar("problem_creating", "error");
      }
    });

  const updateFeedbackMutation = feedbackQuerys.useUpdateFeedback(updateFeedback, {
      onMutate: () => {
        startLoading();
      },
      onSettled: () => {
        stopLoading();
      },
      onError: () => {
        openSnackbar("problem", "error");
      }
    });
  
    const deleteFeedbackMutation = feedbackQuerys.useDeleteFeedback(deleteFeedback, {
      onMutate: () => {
        startLoading();
      },
      onSettled: () => {
        stopLoading();
      },
      onError: () => {
        openSnackbar("problem", "error");
      }});

  const { t } = useTranslation();

  const studentName = createdByName;
  const creatername = course.createdByName;
  const { isCreator } = course;

  const url = `${window.location.href}`;
  

  const handleFeedbackSubmit = async (values) => {
    try {
      await sendFeedbackEmail(
        [memberId],
        studentName,
        creatername,
        values,
        topicName,
        exercise,
        url,
        "a",
      );
      const body = {
        completionId,
        botId,
        courseId,
        courseSectionId,
        courseSectionTopicId,
        exercise,
        feedbackContent: values,
        memberId,
        roleplayId,
        sectionId,
        topicId,
      };

      await createFeedbackMutation.mutateAsync({ body });
      openSnackbar("Feedback sent", "success");
    } catch (err) {
      logger.error(err);
      openSnackbar("Failed to send feedback", "error");
    }
  };

  const handleFeedbackUpdate = async (values) => {
    try {
      await sendFeedbackEmail(
        [memberId],
        studentName,
        creatername,
        values,
        topicName,
        exercise,
        url,
        "updated",
      );
      const body = {
        feedbackContent: values,
      };

      if (completionFeedback.completionId) {
        await updateFeedbackMutation.mutateAsync({
          completionId: completionFeedback.completionId,
          feedbackId: completionFeedback.feedbackId,
          body,
      });
      }
      openSnackbar("Feedback updated", "success");
    } catch (err) {
      logger.error(err);
      openSnackbar("Failed to update feedback", "error");
    }
  };

  const handleFeedbackDelete = async () => {
    try {
      if (completionFeedback.completionId) {
        await deleteFeedbackMutation.mutateAsync({
          completionId: completionFeedback.completionId,
          feedbackId: completionFeedback.feedbackId,
        });
      }
      openSnackbar("Feedback Deleted", "success");
    } catch (err) {
      logger.error(err);
      openSnackbar("Failed to delete feedback", "error");
    }
  };

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
      } = params;
      const result = await tts(text, learnLanguage, voiceName, languageCode);
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStopSpeaking = async (textContainer) => {
    try {
      stopHighlightingSpeechText(textContainer, timeoutArray);
      stop();
      clearQueue();
    } catch (error) {
      logger.error(error);
    }
  };

  return (
    <>
      <ExerciseAttemptScreen
        t={t}
        exerciseAttempt={exerciseAttempt || {}}
        handleSpeak={(s) =>
          handleSpeak({
            ...s,
            learnLanguage: course.learnLang,
          })
        }
        handleStopSpeaking={handleStopSpeaking}
        handleTranslate={(text) =>
          translate(text, course.learnLang, course.forLang)
        }
        subscription={subscription}
        handleFeedbackSubmit={handleFeedbackSubmit}
        studentName={studentName}
        isCreator={isCreator ?? isClassroomCreator ?? false}
        userImage={course.userImage}
        feedbackCreaterName={
          completionFeedback ? completionFeedback.createdByName : " "
        }
        feedbackCreatedDate={
          completionFeedback
            ? date.setUTCMilliseconds(completionFeedback.createdAt)
            : ""
        }
        feedback={completionFeedback ? completionFeedback.feedbackContent : " "}
        handleFeedbackDelete={handleFeedbackDelete}
        feedbackUpdatedDate={
          completionFeedback ? completionFeedback.updatedAt : ""
        }
        handleFeedbackUpdate={handleFeedbackUpdate}
        mainSiteUrl={config.SITE_URL}
        addToQueue={addToQueue}
        addAudioListToQueue={addAudioListToQueue}
        clearQueue={clearQueue}
        stop={stop}
        highlightSpeechText={highlightSpeechText}
        sessionReport={sessionReportQuery.data?.Item}
        sessionReportStorageKey={`session-report-collapsed:${username}`}
        sessionReportNewStorageKey={
          username === memberId
            ? `session-report-seen:${username}:${botId}|${memberId}|${createdAt}`
            : undefined
        }
      />
    </>
  );
}

Attempt.propTypes = {
  navigate: PropTypes.func,
};

export default Attempt;
